/**
 * Inherited rules (owner, 2026-10-02: "Country > State > County conflicts and overrides with county as authoritative within
 * the jurisdiction and inheritance rules and FDA as fallback").
 *
 * 1. Jurisdiction nodes: country → state → health authority → (city program). Each node says how it relates to its parent:
 *    `replaces` (Nevada's districts: NRS 580.210(2)), `amends` (fills what the parent leaves open: Utah's departments, Utah
 *    Code 26A-1-121(3)(b)), `stricter_only` (the parent is a floor: the stricter of the two wins, by the key's comparator), or
 *    `preempted` (the parent wins: federal allergen labeling, 21 U.S.C. 343(w)). A relation can differ per key prefix
 *    (`relationByKey`), e.g. a state OSHA plan replaces federal reporting while the state's food rules amend nothing federal.
 *    A city program is its own node and wins for addresses inside it.
 * 2. Rule values keyed by topic ("cold_holding.max_temp", "food_handler.rule", "posting.snhd-grade-card", "permit.food-permit",
 *    "role.cfpm"), each with a status (law | adopted | amended | guidance | unknown), a source, lastChecked, an effective
 *    window and a scope (permit types, risk levels).
 * 3. The resolver walks from the country down to the most specific node covering the place, applying each relation; with no
 *    law value anywhere it falls back to the most specific guidance (the FDA Food Code at the country node), labeled
 *    "guidance, not confirmed as law here"; with nothing at all it returns `unknown` and who to confirm with. It returns the
 *    value, its status and the provenance chain (the app's "why" line and citations).
 * 4. Conflicts: two values for one key at one node in overlapping windows = a validator error; effective dates switch values
 *    on the pinned date; sources that disagree are kept on the value (`disputedBy`) and flagged, never auto-picked.
 * 5. Audit: a saved record stores the rule ids and versions it was judged by (LogEntry.evaluation.appliedRules), so a later
 *    rule change never rewrites history.
 *
 * Utah and Nevada were migrated onto this model with byte-identical behavior: their shapes (states/<st>-shapes.ts) are the
 * authoring format, converted to rule values here; ruleshapes.ts shapesAt reads every shape through `resolveRule`
 * (proof: scripts/qa/rules-exports.ts, before and after).
 */
import type { AppliedRule, Credential, IsoDate, PermitType } from "./model.ts";
import { departmentsOf, type LocalDepartment } from "./localrules.ts";
import { contactsOf, countyIn, packFor, SUPPORTED_STATES } from "./states/index.ts";
import { loadedStates, onStatesChanged, stateModule } from "./states/access.ts";
import type { Source, UsState } from "./states/types.ts";
import type { AuthorityShapes, Deadline, PostingShape, RuleShapes, Sourced, StateShapes } from "./states/shapes.ts";

// ── Model ────────────────────────────────────────────────────────────────────────────────────────────────────────────

/** `district_office`: a state agency's own district office that inspects some counties directly (New York: no local layer there). */
export type JLevel = "country" | "state" | "authority" | "district_office" | "city";
export type Relation = "replaces" | "amends" | "stricter_only" | "preempted";
export type RuleStatus = "law" | "adopted" | "amended" | "guidance" | "unknown";

export interface JNode {
  id: string;
  level: JLevel;
  name: string;
  parent: string | null;
  /** What it covers inside its parent: counties (an authority), cities (a city program). A state covers its whole pack. */
  covers: { state?: UsState; counties?: readonly string[]; cities?: readonly string[] };
  /** How its rules relate to its parent's. */
  relation: Relation;
  /** A different relation for keys starting with a prefix ("workplace.", "labeling."). */
  relationByKey?: Record<string, Relation>;
  /** Why the relation is what it is. */
  relationSource: Source[];
  /** Who to confirm with, sourced. */
  contact?: { phone?: string; url?: string; address?: string };
  confirmed: boolean;
}

export interface RuleValue<T = unknown> {
  /** Stable id of this rule value ("NV/snhd/inspection.scoring"); `version` goes up when its content changes. */
  id: string;
  version: number;
  key: string;
  node: string;
  status: RuleStatus;
  value?: T;
  source?: Source & { section?: string };
  /** For `unknown`: what was tried. */
  tried?: string;
  lastChecked: IsoDate;
  effectiveFrom?: IsoDate;
  effectiveTo?: IsoDate;
  /**
   * Who it applies to: permit types, risk levels, business types; only these cities, or all but these (a county rule that
   * only adopting cities follow: LA County grades); and chains of at least `minLocations` places (CA SB 68: 20+; NYC: 15+).
   */
  scope?: { permits?: PermitType[]; risk?: string[]; business?: string[]; cities?: string[]; exceptCities?: string[]; minLocations?: number; /** Only with at least this many employees (CA Prop 65: 10+), counted from the roster on the date. */ minEmployees?: number };
  note?: string;
  /** Another source that says something else: flagged on the result, never auto-picked. */
  disputedBy?: { source: Source; says: string }[];
}

export interface ProvenanceStep {
  node: string;
  level: JLevel;
  /** This node's relation to its parent for this key ("root" for the country). */
  relation: Relation | "root";
  status: RuleStatus | "none";
  ruleId?: string;
  version?: number;
  citation?: string;
  url?: string;
  /** Did this step's value become (or stay) the answer? */
  applied: boolean;
  why: string;
}

export interface Resolution<T = unknown> {
  key: string;
  status: RuleStatus;
  value?: T;
  /** The rule value that answered (absent for unknown). */
  rule?: RuleValue<T>;
  /** "guidance, not confirmed as law here" for a guidance answer. */
  label?: string;
  chain: ProvenanceStep[];
  flags: ("disputed" | "no_comparator" | "conditional")[];
  /** When the rule's condition can't be checked (the head count isn't known): "applies if you have 10+ employees". */
  conditional?: string;
  /** For unknown: who to confirm with. */
  confirmWith?: { name: string; phone?: string };
  /** For unknown: what was tried at the most specific node that tried. */
  tried?: { text: string; lastChecked: IsoDate; node: string };
}

export const GUIDANCE_LABEL = "guidance, not confirmed as law here";

export interface Place { state?: string | null; county?: string | null; city?: string | null; /** Places in the business's chain (scope.minLocations). */ chainSize?: number; /** Full- and part-time people on the roster on the date (scope.minEmployees). Absent = not known. */ employees?: number }
export interface World { nodes: readonly JNode[]; rules: readonly RuleValue[] }

// ── Comparators: what "stricter" means per key (stricter_only) ───────────────────────────────────────────────────────

type Cmp = (a: unknown, b: unknown) => unknown;
const lower = (f: (x: any) => number): Cmp => (a, b) => (f(b) < f(a) ? b : a);
const higher = (f: (x: any) => number): Cmp => (a, b) => (f(b) > f(a) ? b : a);
const deadlineDays = (d: Deadline | undefined): number => (!d ? Infinity : d.kind === "before_first_work" ? 0 : d.kind === "within_days" ? d.days : d.kind === "renew_every_years" ? d.years * 365 : -d.days);
/** The stricter of two values for a key, or undefined when the key has no comparator (then the child wins and it's flagged). */
export const COMPARATORS: Record<string, Cmp> = {
  "cold_holding.max_temp": lower((x) => x.maxF),
  "hot_holding.min_temp": higher((x) => x.minF),
  /** Manual sanitizing minimums (states/shapes.ts SanitizerShape): the higher chlorine floor is stricter. */
  "sanitizer.manual_min": higher((x) => x.chlorine.minPpm),
  /** Cooling: the one with less total time to the final temperature is stricter. */
  "cooling.stages": lower((x) => (x.stages as { hours: number }[]).reduce((a, b) => a + b.hours, 0)),
  "mobile.water_tank_min_gallons": higher((x) => x as number),
  "mobile.cooling_on_board": (a, b) => (a === false || b === false ? false : true),
  "event.apply_business_days_ahead": higher((x) => x as number),
  "event.max_consecutive_days": lower((x) => x as number),
  "illness.report": lower((x) => x.withinHours ?? Infinity),
  "food_handler.rule": (a: any, b: any) => (!b?.required ? a : !a?.required ? b : deadlineDays(b.steps.at(-1)?.due) < deadlineDays(a.steps.at(-1)?.due) ? b : a),
};

// ── The resolver ─────────────────────────────────────────────────────────────────────────────────────────────────────

const norm = (c: string) => c.trim().toLowerCase().replace(/\s+(county|co\.?)$/, "").trim();
const inWindow = (v: RuleValue, date: IsoDate) => (!v.effectiveFrom || v.effectiveFrom <= date) && (!v.effectiveTo || date <= v.effectiveTo);
const inScope = (v: RuleValue, s: { permit?: PermitType; risk?: string } | undefined, place: Place) => {
  const sc = v.scope;
  if (!sc) return true;
  const city = place.city ? norm(place.city) : "";
  return (!sc.permits || !s?.permit || sc.permits.includes(s.permit)) && (!sc.risk || !s?.risk || sc.risk.includes(s.risk))
    && (!sc.cities || (!!city && sc.cities.some((c) => norm(c) === city)))
    && (!sc.exceptCities || !city || !sc.exceptCities.some((c) => norm(c) === city))
    && (!sc.minLocations || (place.chainSize ?? 1) >= sc.minLocations)
    // An unknown head count keeps the rule, flagged conditional (never hidden): "applies if you have 10+ employees".
    && (!sc.minEmployees || place.employees === undefined || place.employees >= sc.minEmployees);
};

/** The nodes covering a place, most specific first (city, authority, state, country). */
export function chainFor(world: World, place: Place): JNode[] {
  const st = (place.state && SUPPORTED_STATES.includes(place.state as UsState) ? place.state : "UT") as UsState;
  const stateNode = world.nodes.find((n) => n.level === "state" && n.covers.state === st);
  const county = place.county ? norm(place.county) : "";
  const city = place.city ? norm(place.city) : "";
  const under = (parent: string, level: JLevel, hit: (n: JNode) => boolean) => world.nodes.find((n) => n.parent === parent && n.level === level && hit(n));
  const out: JNode[] = [];
  const auth = stateNode && county ? (under(stateNode.id, "authority", (n) => !!n.covers.counties?.some((c) => norm(c) === county)) ?? under(stateNode.id, "district_office", (n) => !!n.covers.counties?.some((c) => norm(c) === county))) : undefined;
  const cityNode = stateNode && city ? world.nodes.find((n) => n.level === "city" && (n.parent === auth?.id || n.parent === stateNode.id) && !!n.covers.cities?.some((c) => norm(c) === city)) : undefined;
  if (cityNode) out.push(cityNode);
  if (auth) out.push(auth);
  if (stateNode) out.push(stateNode);
  let p = stateNode?.parent;
  while (p) { const n = world.nodes.find((x) => x.id === p); if (!n) break; out.push(n); p = n.parent; }
  return out;
}

export const relationOf = (n: JNode, key: string): Relation => {
  const hit = Object.entries(n.relationByKey ?? {}).filter(([pre]) => key.startsWith(pre)).sort((a, b) => b[0].length - a[0].length)[0];
  return hit ? hit[1] : n.relation;
};

/** Resolve one key for a place in a world, on a date, for a scope. */
export function resolveIn<T = unknown>(world: World, key: string, place: Place, o: { date?: IsoDate; scope?: { permit?: PermitType; risk?: string } } = {}): Resolution<T> {
  const date = o.date ?? "2026-10-02";
  const chain = chainFor(world, place);
  const at = (n: JNode) => world.rules.filter((r) => r.key === key && r.node === n.id && inWindow(r, date) && inScope(r, o.scope, place));
  const steps: ProvenanceStep[] = [];
  const flags: Resolution["flags"] = [];
  let cur: RuleValue | undefined;
  const top = [...chain].reverse();
  for (let i = 0; i < top.length; i++) {
    const n = top[i]!;
    const rel: Relation | "root" = i === 0 ? "root" : relationOf(n, key);
    const vals = at(n);
    const law = vals.find((v) => v.status === "law" || v.status === "adopted" || v.status === "amended");
    const any = vals[0];
    const base = { node: n.id, level: n.level, relation: rel };
    // A node that REPLACES its parent's rules and says "not known here" leaves the key unknown: the parent's rule no longer
    // applies inside it (a Nevada district's regulation supersedes the state's, NRS 580.210(2)).
    if (!law && any?.status === "unknown" && rel === "replaces" && cur) {
      steps.push({ ...base, status: "unknown", ruleId: any.id, version: any.version, applied: false, why: `replaces ${cur.node}'s rule here, and its own isn't known: ${any.tried ?? ""}`.trim() });
      cur = undefined;
      continue;
    }
    if (!law) { steps.push({ ...base, status: any?.status ?? "none", ...(any ? { ruleId: any.id, version: any.version, ...(any.source ? { citation: any.source.citation, url: any.source.url } : {}) } : {}), applied: false, why: any ? (any.status === "guidance" ? "guidance only (used only if no law value is found)" : `not known here: ${any.tried ?? ""}`.trim()) : "no rule here" }); continue; }
    const cite = { ruleId: law.id, version: law.version, ...(law.source ? { citation: law.source.citation, url: law.source.url } : {}) };
    if (!cur || rel === "root") { cur = law; steps.push({ ...base, status: law.status, ...cite, applied: true, why: "the first rule found, from the top" }); continue; }
    if (rel === "preempted") { steps.push({ ...base, status: law.status, ...cite, applied: false, why: `preempted: ${cur.node}'s rule stands` }); continue; }
    if (rel === "stricter_only") {
      const cmp = COMPARATORS[key];
      if (!cmp) { flags.push("no_comparator"); cur = law; steps.push({ ...base, status: law.status, ...cite, applied: true, why: "stricter-only, with no comparator for this key: the more specific rule is used and flagged" }); continue; }
      const pick = cmp(cur.value, law.value) === law.value ? law : cur;
      steps.push({ ...base, status: law.status, ...cite, applied: pick === law, why: pick === law ? "stricter than its parent's, so it applies" : `not stricter than ${cur.node}'s, so the parent's stands` });
      cur = pick;
      continue;
    }
    // replaces / amends: the more specific value wins for this key.
    steps.push({ ...base, status: law.status, ...cite, applied: true, why: rel === "replaces" ? `replaces ${cur.node}'s rule here` : `amends ${cur.node}'s rule here` });
    cur = law;
  }
  // Mark the earlier applied steps that were later overridden.
  if (cur) for (const s of steps) if (s.applied && s.ruleId !== cur.id) s.applied = false;
  if (cur) {
    if (cur.disputedBy?.length) flags.push("disputed");
    const cond = cur.scope?.minEmployees && place.employees === undefined ? `applies if you have ${cur.scope.minEmployees}+ employees` : undefined;
    if (cond) flags.push("conditional");
    return { key, status: cur.status, value: cur.value as T, rule: cur as RuleValue<T>, chain: steps.reverse(), flags, ...(cond ? { conditional: cond } : {}) };
  }
  // No law value: the most specific guidance.
  for (const n of chain) {
    const g = at(n).find((v) => v.status === "guidance");
    if (g) {
      const s = steps.find((x) => x.ruleId === g.id);
      if (s) { s.applied = true; s.why = "no law value found: guidance is used"; }
      if (g.disputedBy?.length) flags.push("disputed");
      return { key, status: "guidance", value: g.value as T, rule: g as RuleValue<T>, label: GUIDANCE_LABEL, chain: steps.reverse(), flags };
    }
  }
  // Nothing: unknown, and who to confirm with (the most specific node with a contact).
  const tried = chain.map((n) => at(n).find((v) => v.status === "unknown")).find(Boolean);
  const who = chain.find((n) => n.contact?.phone || n.contact?.url) ?? chain[0];
  return { key, status: "unknown", chain: steps.reverse(), flags, ...(who ? { confirmWith: { name: who.name, ...(who.contact?.phone ? { phone: who.contact.phone } : {}) } } : {}),
    ...(tried ? { tried: { text: tried.tried ?? "", lastChecked: tried.lastChecked, node: tried.node } } : {}) };
}

/** Every key with any value along a place's chain, in order: state's first (in data order), then the more specific nodes'. */
export function keysIn(world: World, place: Place, prefix: string): string[] {
  const chain = chainFor(world, place).filter((n) => n.level !== "country");
  const out: string[] = [];
  for (const n of [...chain].reverse()) for (const r of world.rules) if (r.node === n.id && r.key.startsWith(prefix) && !out.includes(r.key)) out.push(r.key);
  return out;
}

// ── Validation ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** Problems in a world: duplicate values in overlapping windows, unknown nodes, unsourced values, unknowns that tried nothing. */
export function worldProblems(world: World): string[] {
  const out: string[] = [];
  const ids = new Set(world.nodes.map((n) => n.id));
  for (const n of world.nodes) {
    if (n.parent && !ids.has(n.parent)) out.push(`${n.id}: parent ${n.parent} is not a node`);
    if (n.parent && !n.relationSource.length) out.push(`${n.id}: relation "${n.relation}" has no source`);
  }
  const overlap = (a: RuleValue, b: RuleValue) => (a.effectiveFrom ?? "0000") <= (b.effectiveTo ?? "9999") && (b.effectiveFrom ?? "0000") <= (a.effectiveTo ?? "9999");
  const sameScope = (a: RuleValue, b: RuleValue) => JSON.stringify(a.scope ?? {}) === JSON.stringify(b.scope ?? {});
  const seen = new Map<string, RuleValue[]>();
  for (const r of world.rules) {
    if (!ids.has(r.node)) out.push(`${r.id}: node ${r.node} is not a node`);
    if (r.status === "unknown") { if (!r.tried?.trim()) out.push(`${r.id}: unknown without what was tried`); }
    else if (!r.source?.citation || !/^https?:\/\//.test(r.source.url ?? "")) out.push(`${r.id}: ${r.status} value without a citation and URL`);
    const k = `${r.node}|${r.key}`;
    for (const o of seen.get(k) ?? []) if (overlap(o, r) && sameScope(o, r)) out.push(`${r.node} ${r.key}: two values in the same window (${o.id}, ${r.id})`);
    seen.set(k, [...(seen.get(k) ?? []), r]);
  }
  return out;
}

// ── Utah and Nevada, migrated: nodes from the packs, values from the shapes ─────────────────────────────────────────

const D = "2026-10-02";
const FC2022 = "https://www.fda.gov/media/164194/download";
const src = (citation: string, url: string, quote?: string, lastChecked = D): Source => ({ citation, url, ...(quote ? { quote } : {}), lastChecked });

/** The country node: real federal law (OSHA's reporting rule, allergen labeling) and the FDA's model code as guidance. */
const US: JNode = { id: "US", level: "country", name: "United States", parent: null, covers: {}, relation: "amends", relationSource: [], confirmed: true };

import { REL } from "./states/relations-data.ts";
/** A non-Utah state's local module, from its loaded bundle (the node builders below run only for loaded states). */
const NY_DEPARTMENT_KIND = (): typeof import("./states/ny-local.ts").NY_DEPARTMENT_KIND => stateModule<typeof import("./states/ny-local.ts")>("NY", "local").NY_DEPARTMENT_KIND;
const OR_DEPARTMENT_KIND = (): typeof import("./states/or-local.ts").OR_DEPARTMENT_KIND => stateModule<typeof import("./states/or-local.ts")>("OR", "local").OR_DEPARTMENT_KIND;
const TX_DEPARTMENT_KIND = (): typeof import("./states/tx-local.ts").TX_DEPARTMENT_KIND => stateModule<typeof import("./states/tx-local.ts")>("TX", "local").TX_DEPARTMENT_KIND;

function stateNode(st: UsState): JNode {
  const p = packFor(st);
  return {
    id: st, level: "state", name: p.name, parent: "US", covers: { state: st }, relation: "amends",
    // A state OSHA plan replaces federal injury reporting; federal allergen labeling preempts the state's.
    // A federal-plan state (New York for private employers) keeps federal OSHA's rule: no workplace replacement.
    relationByKey: p.workplace.plan === "state" ? { "workplace.": "replaces", "labeling.": "preempted" } : { "labeling.": "preempted" },
    relationSource: [st === "UT" ? REL.UT_FED_OSHA! : st === "CA" ? REL.CA_CAL_OSHA! : st === "NY" ? REL.NY_FED_OSHA! : st === "OR" ? REL.OR_OSHA! : st === "FL" ? REL.FL_FED_OSHA! : st === "TX" ? REL.TX_FED_OSHA! : st === "MA" ? REL.MA_FED_OSHA! : REL.NV_FED_OSHA!, REL.FALCPA!],
    contact: { ...(p.stateAgency.phones[0] ? { phone: p.stateAgency.phones[0].number } : {}), url: p.stateAgency.url },
    confirmed: true,
  };
}

/** California's three counties with a pre-2009 food handler card program (HSC 113948(f)). */
const CA_OWN_CARD = new Set(["riverside", "san-bernardino", "san-diego"]);

/**
 * A California agency: CalCode occupies the field (HSC 113705), so the node is `preempted`, and only what the law lets it add is
 * opened: its grading system and the card or placard it posts (113709), home kitchens where it opted in (114367), its own
 * permit and opening steps, and, in the three pre-2009 counties, its own food handler card (113948(f)).
 */
function caAuthorityNode(d: LocalDepartment, phone: string | undefined): JNode {
  const relationByKey: Record<string, Relation> = { "inspection.scoring": "amends", "posting.": "amends", "home.": "amends", "permit.": "amends", "permit_renewal.": "amends", "pre_opening.": "amends", "inspection.frequency": "amends" };
  if (CA_OWN_CARD.has(d.id)) { relationByKey["food_handler."] = "replaces"; relationByKey["role.food_handler"] = "replaces"; }
  const rs = [REL.CA_FIELD!, REL.CA_GRADING!, REL.CA_MEHKO!, ...(CA_OWN_CARD.has(d.id) ? [REL.CA_LOCAL_CARD!] : [])];
  return { id: `CA/${d.id}`, level: "authority", name: d.name, parent: "CA", covers: { counties: d.counties }, relation: "preempted", relationByKey, relationSource: rs, contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
}

/**
 * A New York office: a county health department or NYC (`amends`: a county sanitary code may add rules "not inconsistent with"
 * the state's, PHL 347; NYC's Article 81 applies "in addition to" it, 81.01), or a NYSDOH district office (the state's own
 * office: level district_office, no local layer).
 */
function nyAuthorityNode(d: LocalDepartment, phone: string | undefined): JNode {
  const kind = NY_DEPARTMENT_KIND()[d.id] ?? "county";
  return { id: `NY/${d.id}`, level: kind === "district_office" ? "district_office" : "authority", name: d.name, parent: "NY", covers: { counties: d.counties }, relation: "amends",
    relationSource: [kind === "nyc" ? REL.NY_NYC! : kind === "district_office" ? REL.NY_DISTRICT! : REL.NY_PHL347!], contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
}

/**
 * An Oregon office runs the state's rule for OHA (ORS 624.510): `amends`, with no local layer except Multnomah's own code
 * (MCC Chapter 21). OHA's own office for Curry and Wallowa is the state's (level district_office, no local layer).
 */
function orAuthorityNode(d: LocalDepartment, phone: string | undefined): JNode {
  const kind = OR_DEPARTMENT_KIND()[d.id] ?? "county";
  return { id: `OR/${d.id}`, level: kind === "state" ? "district_office" : "authority", name: d.name, parent: "OR", covers: { counties: d.counties }, relation: "amends",
    relationSource: [kind === "state" ? REL.OR_STATE_RUN! : d.id === "multnomah" ? REL.OR_MULTNOMAH! : REL.OR_DELEGATED!], contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
}

/** A Florida DBPR district office: the state's own office (level district_office), local food rules preempted (509.032(7)). */
function flAuthorityNode(d: LocalDepartment, phone: string | undefined): JNode {
  return { id: `FL/${d.id}`, level: "district_office", name: d.name, parent: "FL", covers: { counties: d.counties }, relation: "preempted",
    relationSource: [REL.FL_PREEMPTED!], contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
}

/**
 * A Texas office. A city's own program may be stricter than state law (`stricter_only`), except for what the 2025 laws cap
 * (`preempted`: food handler and manager cards, allergen rules, cottage food, food trucks; HSC 437.0045, 438.046(c), 438.1055,
 * 437.027(e), 437.0192, 437B). A county or public health district enforces state law only (`preempted`). DSHS's own regional
 * office is the state's (level district_office).
 */
const TX_CAPPED: Record<string, Relation> = { "food_handler.": "preempted", "manager.": "preempted", "role.food_handler": "preempted", "role.cfpm": "preempted", "role.allergen_awareness": "preempted", "home.": "preempted", "mobile.": "preempted" };
function txAuthorityNode(d: LocalDepartment, phone: string | undefined): JNode {
  const kind = TX_DEPARTMENT_KIND()[d.id] ?? "county";
  const base = { id: `TX/${d.id}`, name: d.name, parent: "TX", covers: { counties: d.counties }, contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
  if (kind === "city") return { ...base, level: "authority", relation: "stricter_only", relationByKey: TX_CAPPED, relationSource: [REL.TX_CITY!, REL.TX_CITY_CAPS!] };
  return { ...base, level: kind === "state" ? "district_office" : "authority", relation: "preempted", relationSource: [kind === "state" ? REL.TX_DSHS! : REL.TX_COUNTY!] };
}

/** A Massachusetts board of health: 105 CMR 590 is the floor, and the board may add stricter rules (`stricter_only`; c.111 §31, §127A). */
function maAuthorityNode(d: LocalDepartment, phone: string | undefined): JNode {
  return { id: `MA/${d.id}`, level: "authority", name: d.name, parent: "MA", covers: { counties: d.counties }, relation: "stricter_only",
    relationSource: [REL.MA_BOARD!], contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
}

function authorityNode(st: UsState, d: LocalDepartment): JNode {
  if (st === "CA") return caAuthorityNode(d, contactsOf(st).find((c) => c.department === d.id)?.phone);
  if (st === "NY") return nyAuthorityNode(d, contactsOf(st).find((c) => c.department === d.id)?.phone);
  if (st === "MA") return maAuthorityNode(d, contactsOf(st).find((c) => c.department === d.id)?.phone);
  if (st === "TX") return txAuthorityNode(d, contactsOf(st).find((c) => c.department === d.id && !c.counties)?.phone ?? contactsOf(st).find((c) => c.department === d.id)?.phone);
  if (st === "FL") return flAuthorityNode(d, contactsOf(st).find((c) => c.department === d.id)?.phone);
  if (st === "OR") return orAuthorityNode(d, contactsOf(st).find((c) => c.department === d.id)?.phone);
  const phone = contactsOf(st).find((c) => c.department === d.id && !c.counties)?.phone ?? contactsOf(st).find((c) => c.department === d.id)?.phone;
  // Nevada: SNHD and Washoe's district regulations replace NAC 446 (NRS 580.210(2)); the state program, Carson City HHS (local code
  // under NAC 446) and the Central Nevada Health District (its own regulation's adoption not confirmed) amend it.
  const replaces = st === "NV" && (d.id === "snhd" || d.id === "nnph");
  const relation: Relation = replaces ? "replaces" : "amends";
  const rs = st !== "NV" ? REL.UT_LOCAL! : replaces ? REL.NV_DISTRICT! : d.id === "carson-city-hhs" ? REL.NV_CARSON! : d.id === "cnhd" ? REL.NV_CNHD! : REL.NV_NDA!;
  return { id: `${st}/${d.id}`, level: "authority", name: d.name, parent: st, covers: { counties: d.counties }, relation, relationSource: [rs], contact: { ...(phone ? { phone } : {}), url: d.url }, confirmed: true };
}

/** A shape key → rule key. */
const KEY: Record<string, string> = {
  foodHandler: "food_handler.rule", manager: "manager.rule", scoring: "inspection.scoring", illness: "illness.report", coldHold: "cold_holding.max_temp", sanitizer: "sanitizer.manual_min",
  "mobile.waterTankMinGallons": "mobile.water_tank_min_gallons", "mobile.coolingOnBoard": "mobile.cooling_on_board", "mobile.hoursPerSpot": "mobile.hours_per_spot", "mobile.routeLog": "mobile.route_log",
  "home.model": "home.model", "home.privateHomeFoodBusinessBarred": "home.private_home_food_business_barred",
  "events.applyBusinessDaysAhead": "event.apply_business_days_ahead", "events.maxConsecutiveDays": "event.max_consecutive_days", "events.boothMaxSqFt": "event.booth_max_sqft",
};
export const RULE_KEY = KEY;

/**
 * law, adopted (a Food Code section adopted by reference, where the state's pack says it adopts by reference) or guidance (our
 * recommendation), from the value and its citation.
 */
export function statusOf(citation: string, value: unknown, st: UsState): RuleStatus {
  if (value === "recommended" || (value && typeof value === "object" && (value as { level?: string }).level === "recommended")) return "guidance";
  const fc = packFor(st).foodCode;
  const ruleToken = fc.rule.split(" ").pop() ?? "";
  const adopts = !!fc.adoptsByReference && /Food Code/.test(citation) && !!ruleToken && citation.includes(ruleToken);
  return /adopted by|\(the 2022 Food Code\)/.test(citation) || adopts ? "adopted" : "law";
}

/** One shape layer (state or authority) → rule values at its node. */
function layerRules(node: string, layer: Partial<RuleShapes> | AuthorityShapes): RuleValue[] {
  const out: RuleValue[] = [];
  const add = (key: string, s: Sourced<unknown>) => {
    const id = `${node}/${key}`;
    if (s.status === "unknown") out.push({ id, version: 1, key, node, status: "unknown", tried: s.tried, lastChecked: s.lastChecked });
    else out.push({ id, version: 1, key, node, status: statusOf(s.source.citation, s.value, node.split("/")[0] as UsState), value: s.value, source: s.source, lastChecked: s.source.lastChecked, ...(s.note ? { note: s.note } : {}) });
  };
  for (const [k, v] of Object.entries(layer)) {
    if (k === "postings") for (const p of v as PostingShape[]) out.push({ id: `${node}/posting.${p.id}`, version: 1, key: `posting.${p.id}`, node, status: statusOf(p.source.citation, p, node.split("/")[0] as UsState), value: p, source: p.source, lastChecked: p.source.lastChecked });
    else if (k === "permits") for (const [pt, s] of Object.entries(v as Record<string, Sourced<Deadline>>)) add(`permit_renewal.${pt}`, s);
    else if (k === "edition") for (const [rid, s] of Object.entries(v as Record<string, Sourced<unknown>>)) add(`edition.${rid}`, s);
    // What the law requires of a built-in check (shapes.ts CheckLawShape): "check.<rule key>", read by ruleshapes.ts checkLawsAt.
    else if (k === "checks") for (const [ck, s] of Object.entries(v as Record<string, Sourced<unknown>>)) add(`check.${ck}`, s);
    else if (k === "mobile" || k === "home" || k === "events") for (const [f, s] of Object.entries(v as Record<string, Sourced<unknown>>)) add(KEY[`${k}.${f}`]!, s);
    else add(KEY[k]!, v as Sourced<unknown>);
  }
  return out;
}

/** Rule values from a state's shapes: the state layer at the state node, each authority layer at its node. */
export function rulesFromShapes(st: UsState, shapes: StateShapes): RuleValue[] {
  return [...layerRules(st, shapes.state), ...Object.entries(shapes.authorities).flatMap(([dep, layer]) => layerRules(`${st}/${dep}`, layer))];
}

/** The country's values: the FDA Food Code 2022 model as guidance (a fallback only), and federal law. */
export const COUNTRY_RULES: RuleValue[] = [
  { id: "US/cold_holding.max_temp", version: 1, key: "cold_holding.max_temp", node: "US", status: "guidance", value: { maxF: 41 }, source: { ...src("FDA Food Code 2022 3-501.16(A)(2) (model code)", FC2022, "(2) At 5°C (41°F) or less."), section: "3-501.16(A)(2)" }, lastChecked: D },
  { id: "US/illness.report", version: 1, key: "illness.report", node: "US", status: "guidance", value: { withinHours: null, tell: "the regulatory authority" }, source: { ...src("FDA Food Code 2022 2-201.11(B) (model code)", FC2022, "(B) The person in charge shall notify the regulatory authority when a food employee is:"), section: "2-201.11(B)" }, lastChecked: D },
  { id: "US/manager.rule", version: 1, key: "manager.rule", node: "US", status: "guidance", value: { rule: "pic_must_be_certified" }, source: { ...src("FDA Food Code 2022 2-102.12(A) (model code)", FC2022, "The PERSON IN CHARGE shall be a certified FOOD protection manager who has shown proficiency of required information through passing a test that is part of an ACCREDITED PROGRAM."), section: "2-102.12(A)" }, lastChecked: D },
  { id: "US/event.max_consecutive_days", version: 1, key: "event.max_consecutive_days", node: "US", status: "guidance", value: 14, source: { ...src("FDA Food Code 2022 1-201.10(B) (model code)", FC2022, "operates for a period of no more than 14 consecutive days"), section: "1-201.10(B)" }, lastChecked: D },
  { id: "US/workplace.injury_report", version: 1, key: "workplace.injury_report", node: "US", status: "law", value: { deathHours: 8, hospitalHours: 24, agency: "federal OSHA" }, source: { ...src("29 CFR 1904.39(a)(1)–(2)", "https://www.law.cornell.edu/cfr/text/29/1904.39", "Within eight (8) hours after the death of any employee as a result of a work-related incident, you must report the fatality to the Occupational Safety and Health Administration (OSHA)"), section: "1904.39(a)" }, lastChecked: D },
  { id: "US/labeling.major_allergens", version: 1, key: "labeling.major_allergens", node: "US", status: "law", value: { packagedFood: "Contains statement or the allergen's source in the ingredient list" }, source: { ...src("21 U.S.C. 343(w)(1) (FALCPA)", "https://www.law.cornell.edu/uscode/text/21/343", "(w) Major food allergen labeling requirements"), section: "343(w)" }, lastChecked: D },
];

/** State OSHA plans: their own reporting rules replace 29 CFR 1904.39 (the pack's workplace facts). */
function workplaceRule(st: UsState): RuleValue {
  const w = packFor(st).workplace;
  return { id: `${st}/workplace.injury_report`, version: 1, key: "workplace.injury_report", node: st, status: "law", value: { line: w.reportLine, agency: w.agency.short, phone: w.agency.phones[0]?.number }, source: w.source, lastChecked: w.source.lastChecked };
}

let BUILT: World | undefined;
// A state that loads later adds its nodes and rules: the next use rebuilds the world.
onStatesChanged(() => { BUILT = undefined; });
const providers: (() => readonly RuleValue[])[] = [];
/** Add rule values to the default world (the permit catalog and role module registers its own; built lazily on first use). */
export function registerRules(provide: () => readonly RuleValue[]): void { providers.push(provide); BUILT = undefined; }

/** The default world: the US and every loaded state (Utah always) with their authorities, and every rule value. */
export function defaultWorld(shapes: Record<UsState, StateShapes>): World {
  if (BUILT) return BUILT;
  const nodes: JNode[] = [US, ...loadedStates().flatMap((st) => [stateNode(st), ...departmentsOf(st).map((d) => authorityNode(st, d))])];
  // A state OSHA plan's own reporting rule; a federal-plan state (New York) keeps the country's 29 CFR 1904.39.
  const rules: RuleValue[] = [...COUNTRY_RULES, ...loadedStates().flatMap((st) => [...rulesFromShapes(st, shapes[st]), ...(packFor(st).workplace.plan === "state" ? [workplaceRule(st)] : [])]), ...providers.flatMap((p) => p())];
  BUILT = { nodes, rules };
  return BUILT;
}

/** A place's state, defaulted like packFor. */
export const placeStateOf = (p: Place): UsState => (p.state && SUPPORTED_STATES.includes(p.state as UsState) ? (p.state as UsState) : "UT");
/** The county as the pack spells it, or "" (an unknown county is no county: state rules only). */
export const placeCounty = (p: Place): string => countyIn(placeStateOf(p), p.county) ?? "";

/** A credential that counts for a role, valid today. */
export const holds = (creds: readonly Pick<Credential, "kind" | "expiresOn">[], kind: Credential["kind"], today: IsoDate) => creds.some((c) => c.kind === kind && (!c.expiresOn || c.expiresOn >= today));

// ── Audit: the rules a record was judged by ──────────────────────────────────────────────────────────────────────────

/** The rule keys each log is judged by (its limits come from these). */
export const TEMPLATE_RULE_KEYS: Record<string, readonly string[]> = {
  // Hot holding and cooling are judged by the place's law too (placelimits.ts: New York 140°F hot, 45°F by the end of cooling).
  cold_units: ["cold_holding.max_temp"], temp_cook_hold: ["cold_holding.max_temp", "hot_holding.min_temp"], tcs_items: ["cold_holding.max_temp"], receiving: ["cold_holding.max_temp"], transport: ["cold_holding.max_temp"],
  transport_temp: ["cold_holding.max_temp", "hot_holding.min_temp"], cooling: ["cooling.stages"],
  // The sanitizer log is judged by a state's own minimum where it has one (California; placelimits.ts): stamped only there.
  sanitizer: ["sanitizer.manual_min"],
  truck_tank: ["mobile.water_tank_min_gallons"], truck_spot: ["mobile.hours_per_spot"], route_sheet: ["mobile.route_log"],
};

/** The applied-rule stamp for a record of this log at this place on this date (empty when the log has no rule keys). */
export function appliedRulesIn(world: World, templateId: string, place: Place, date: IsoDate): AppliedRule[] {
  return (TEMPLATE_RULE_KEYS[templateId] ?? []).flatMap((key) => {
    const r = resolveIn(world, key, place, { date });
    return r.rule && r.status !== "unknown" ? [{ key, ruleId: r.rule.id, version: r.rule.version, status: r.status }] : [];
  });
}

/** An evaluation with its stamp: a new record gets today's rules; an edited one keeps the stamp it was saved with. */
export function stampEvaluation<E extends { ok: boolean; appliedRules?: AppliedRule[] }>(e: E, stamp: AppliedRule[] | undefined): E {
  return stamp?.length ? { ...e, appliedRules: stamp } : e;
}

// ── The law, and what CleanCleaver recommends beside it ──────────────────────────────────────────────────────────────

export interface LimitView<T = unknown> {
  /** The jurisdiction's own sourced value: compliance is judged against this. */
  law: Resolution<T>;
  /** A stricter recommendation (the FDA Food Code model, as guidance), shown beside the law and labeled. Absent when the law is as strict. */
  recommend?: { value: T; citation: string; line: string };
}

/**
 * A limit as the app shows it (coordinator, 2026-10-03): the law here judges compliance; where the FDA model is stricter
 * (e.g. New York outside NYC: 45°F by law), it's shown beside it: "Law here: 45°F. We recommend 41°F, the FDA Food Code."
 * `describe` turns a value into words ("45°F").
 */
export function limitWithRecommendation<T>(world: World, key: string, place: Place, describe: (v: T) => string, o: { date?: IsoDate } = {}): LimitView<T> {
  const law = resolveIn<T>(world, key, place, o);
  const g = world.rules.find((r) => r.node === "US" && r.key === key && r.status === "guidance" && inWindow(r, o.date ?? "2026-10-02"));
  const cmp = COMPARATORS[key];
  if (!g || !cmp || law.status === "guidance" || law.status === "unknown" || law.value === undefined) return { law };
  const stricter = cmp(law.value, g.value) === g.value && JSON.stringify(g.value) !== JSON.stringify(law.value);
  if (!stricter) return { law };
  const cite = g.source?.citation ?? "FDA Food Code";
  return { law, recommend: { value: g.value as T, citation: cite, line: `Law here: ${describe(law.value)}. We recommend ${describe(g.value as T)}, the FDA Food Code (${cite}).` } };
}
